import type { Alignment, ClipDims, DrapeSampler } from '@/core/alignment';
import { atom } from 'jotai';

/**
 * Source-agnostic alignment registry. Producers (LandXML.tsx, GeoJson.tsx)
 * register the alignments they own under an `ownerKey` (their layer id);
 * consumers (walk toolbar, overlay, properties drawers) only read
 * `alignmentsAtom`.
 */
const registryAtom = atom<{ byKey: Map<string, Alignment>; byOwner: Map<string, string[]> }>({
  byKey: new Map(),
  byOwner: new Map(),
});

export const alignmentsAtom = atom((get) => get(registryAtom).byKey);

export const registerAlignmentsAtom = atom(
  null,
  (get, set, { ownerKey, alignments }: { ownerKey: string; alignments: Alignment[] }) => {
    const prev = get(registryAtom);
    const byKey = new Map(prev.byKey);
    const byOwner = new Map(prev.byOwner);
    for (const k of byOwner.get(ownerKey) ?? []) byKey.delete(k);
    for (const a of alignments) byKey.set(a.key, a);
    byOwner.set(ownerKey, alignments.map((a) => a.key));
    set(registryAtom, { byKey, byOwner });
    const walk = get(alignmentWalkAtom);
    if (walk && !byKey.has(walk.key)) set(alignmentWalkAtom, null);
    const sel = get(selectedAlignmentKeyAtom);
    if (sel && !byKey.has(sel)) set(selectedAlignmentKeyAtom, null);
  },
);

export const unregisterAlignmentsAtom = atom(null, (_get, set, ownerKey: string) => {
  set(registerAlignmentsAtom, { ownerKey, alignments: [] });
});

/** Re-exported for consumers that only import from the atom module. */
export type { DrapeSampler } from '@/core/alignment';
/** `(x, y) → z` on a loaded TIN, keyed by the layer id that owns the TIN. */
export const alignmentDrapeSourcesAtom = atom<Map<string, DrapeSampler>>(new Map());

export const DEFAULT_WALK_STEP = 20;
/** Thin cross-section slice: 2 m along, 40 m across, 20 m tall. */
export const DEFAULT_WALK_CLIP: ClipDims = { length: 2, width: 40, height: 20 };

export interface AlignmentWalkState {
  key: string;
  /** Internal station. */
  station: number;
  /** Metres per ◀ / ▶ click. */
  step: number;
  snapKeyPoints: boolean;
  follow: boolean;
  /** Clip box follows the station as a cross-section slice; null/undefined = off. */
  clip?: ClipDims | null;
}
export const alignmentWalkAtom = atom<AlignmentWalkState | null>(null);

/** Highlighted (picked) alignment — may differ from the walked one. */
export const selectedAlignmentKeyAtom = atom<string | null>(null);
